import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaGoogle, FaFacebook, FaLinkedin } from 'react-icons/fa';
import KruozAPI from '../../../KruozAPI';
import { useNavigate } from "react-router-dom";

const ContactInfoStep = ({ formData, handleInputChange, errors, user, handleSocialLogin, verifyEmail, login }) => {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const handleVerifyCode = async() => {
    setIsLoading(true);
    setError('');
  
    try {
      const encodedEmail = encodeURIComponent(formData.email);
      
      const verifyResponse = await KruozAPI.get(`api/User/VerifyCode?email=${encodedEmail}&code=${verificationCode}&role=Customer`);
  
      if (verifyResponse.status === 200) {
        const user = verifyResponse.data;

        //set customerUserId in formData with user.id
        formData.customerUserId = user.id;
        // Call the token endpoint
        const tokenResponse = await KruozAPI.get(`/User/token?userId=${user.id}&companyId=${user.companyId}`);
        
        if (tokenResponse.status === 200) {
          // Update the user object with the received token
          user.token = tokenResponse.data.token;
          login(user);
          // Store the updated user object in sessionStorage
          localStorage.setItem('user', JSON.stringify(user));
        } else {
          throw new Error('Failed to get token');
        }
      } else {
        throw new Error('Failed to verify code');
      }
    } catch (error) {
      console.error('Error details:', error);
      let errorMessage = 'Failed to verify code. Please try again.';
      if (error.response && error.response.data) {
        // If the error response has a data property, use it as the error message
        errorMessage = typeof error.response.data === 'string' 
          ? error.response.data 
          : JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendVerificationEmail = async () => {
    setIsLoading(true);
    setError("");

    try {
      const encodedEmail = encodeURIComponent(formData.email);
      const timeZone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      const response = await KruozAPI.post(
        `/User/SendVerificationCode?email=${encodedEmail}&role=Customer&timeZone=${timeZone}`
      );

      if (response.status === 200) {
        setIsCodeSent(true);
        setResendDisabled(true);
        startResendCountdown();
        // You might want to show a success message here
      } else {
        throw new Error("Failed to send verification code");
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to send verification code. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const startResendCountdown = () => {
    setResendCountdown(60);
    const interval = setInterval(() => {
      setResendCountdown((prevCount) => {
        if (prevCount <= 1) {
          clearInterval(interval);
          setResendDisabled(false);
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);
  };
  if(user)
  {
    formData.email = user.email;
    formData.customerUserId = user.id;
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col space-y-4">
          <button
            onClick={() => handleSocialLogin('google')}
            className="flex items-center justify-center bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
          >
            <FaGoogle className="mr-2" /> Login with Google
          </button>
          <button
            onClick={() => handleSocialLogin('facebook')}
            className="flex items-center justify-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            <FaFacebook className="mr-2" /> Login with Facebook
          </button>
          <button
            onClick={() => handleSocialLogin('linkedin')}
            className="flex items-center justify-center bg-blue-700 text-white px-4 py-2 rounded hover:bg-blue-800 transition-colors"
          >
            <FaLinkedin className="mr-2" /> Login with LinkedIn
          </button>
        </div>

        <div className="mt-4">
          <p className="text-center font-medium">Or</p>
        </div>
        <div>
        {!isCodeSent && <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm mb-5"
            required
          />
          {errors.email && <p className="text-red-500 mt-1">{errors.email}</p>}
        </div>}

        {!isCodeSent ? (
          <button
            onClick={handleSendVerificationEmail}
            className="w-full bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
            disabled={isLoading}
          >
             {isLoading ? "Sending..." : "Send Verification Code"}
          </button>
        ) : (
          <div className="space-y-4">
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter verification code"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
            />
            <button
              onClick={handleVerifyCode}
              className="w-full bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Verify Code"}
            </button>
            <button
              onClick={handleSendVerificationEmail}
              className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              disabled={resendDisabled || isLoading}
            >
              {resendDisabled
                ? `Resend Code (${resendCountdown}s)`
                : isLoading
                ? "Sending..."
                : "Resend Verification Code"}
            </button>
          </div>
        )}
      </div>
      {error && (
        <div className="mt-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
          {typeof error === "string"
            ? error
            : "An error occurred. Please try again."}
        </div>
      )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
          Full Name
        </label>
        <input
          type="text"
          id="fullName"
          name="fullName"
          value={formData.fullName}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          required
        />
        {errors.fullName && <p className="text-red-500 mt-1">{errors.fullName}</p>}
      </div>

      <div>
        <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700">
          Mobile Number
        </label>
        <input
          type="tel"
          id="mobileNumber"
          name="mobileNumber"
          value={formData.mobileNumber}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          required
        />
        {errors.mobileNumber && <p className="text-red-500 mt-1">{errors.mobileNumber}</p>}
      </div>

      <div>
        <label htmlFor="homeNumber" className="block text-sm font-medium text-gray-700">
          Home Number (Optional)
        </label>
        <input
          type="tel"
          id="homeNumber"
          name="homeNumber"
          value={formData.homeNumber}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
        />
      </div>

      <div>
        <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
          Business Name (Optional)
        </label>
        <input
          type="text"
          id="businessName"
          name="businessName"
          value={formData.businessName}
          onChange={handleInputChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium">Second Contact (Optional)</h3>
        <div>
          <label htmlFor="secondContactName" className="block text-sm font-medium text-gray-700">
            Name
          </label>
          <input
            type="text"
            id="secondContactName"
            name="secondContactName"
            value={formData.secondContactName}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="secondContactMobile" className="block text-sm font-medium text-gray-700">
            Mobile
          </label>
          <input
            type="tel"
            id="secondContactMobile"
            name="secondContactMobile"
            value={formData.secondContactMobile}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="secondContactEmail" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            id="secondContactEmail"
            name="secondContactEmail"
            value={formData.secondContactEmail}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
          />
        </div>
      </div>
    </div>
  );
};

ContactInfoStep.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  user: PropTypes.bool.isRequired,
  handleSocialLogin: PropTypes.func.isRequired,
  verifyEmail: PropTypes.func.isRequired,
};

export default ContactInfoStep;